<template>
  <!-- Show Advance Payment Details in a Popup Modal -->
  <div v-if="showModal" class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content" ref="modalContent">
      <div v-if="isLoadingDetails" class="loading-container text-center py-5">
         <span class="spinner-border text-primary" role="status"></span>
         <p class="mt-3">Loading Receipt Details...</p>
      </div>
      <div v-else-if="fetchError" class="error-container text-center py-5">
         <p class="text-danger">{{ fetchError }}</p>
         <button @click="$emit('close')" class="btn btn-secondary">Close</button>
      </div>
      <div v-else class="receipt-container">
        <!-- Logo & Header -->
        <div class="receipt-header">
          <img src="../../assets/images/logo/gas_logo.png" alt="Laugfs Gas Logo" class="logo">
          <div class="header-right text-right">
             <h2 class="invoice-title">Advance Payment Receipt</h2>
             <p class="header-subtitle"><strong>Receipt No:</strong> {{ adPayment.advance_payments_no || '—' }}</p>
             <p class="header-subtitle"><strong>Date:</strong> {{ formatDateTime(adPayment.ad_paid_at || adPayment.paid_at || adPayment.created_at) }}</p>
          </div>
        </div>

        <hr class="divider" />

        <!-- Info Section -->
        <div class="info-section">
          <!-- Distributor Details -->
          <div class="info-box">
            <h4>Distributor Details</h4>
            <p><strong>Name:</strong> {{ distributorDetails?.businessName || distributorDetails?.full_name || distributor.full_name }}</p>
            <p><strong>Address:</strong> {{ distributorDetails?.address || distributor.address1 }}</p>
            <p><strong>Mobile:</strong> {{ distributorDetails?.mobile || distributor.mobile }}</p>
          </div>

          <!-- Dealer Details -->
          <div class="info-box text-right right-align-box">
            <h4>Dealer Details</h4>
            <p><strong>Name:</strong> {{ dealerDetails?.businessName || adPayment.dealer_name || '—' }}</p>
            <p><strong>Address:</strong> {{ dealerDetails?.address || '—' }}</p>
            <p><strong>Mobile:</strong> {{ dealerDetails?.mobile || '—' }}</p>
          </div>
        </div>

        <hr class="divider" />

        <!-- Payment Details -->
        <div class="payment-details">
          <h4>Payment Info</h4>
          <p><strong>Method:</strong> {{ adPayment.ad_pay_method || adPayment.pay_method }}</p>
          
          <template v-if="(adPayment.ad_pay_method || adPayment.pay_method) === 'Cheque'">
            <p><strong>Cheque No:</strong> {{ adPayment.ad_cheque_no || adPayment.cheque_no || '—' }}</p>
            <p><strong>Bank:</strong> {{ adPayment.bank_name || '—' }}</p>
            <p><strong>Branch:</strong> {{ adPayment.branch_name || '—' }}</p>
          </template>
          <template v-else-if="(adPayment.ad_pay_method || adPayment.pay_method) === 'Bank Transfer'">
            <p><strong>Account No:</strong> {{ adPayment.ad_bank_acc_no || adPayment.distributor_account || '—' }}</p>
            <p v-if="adPayment.distributor_account_name"><strong>Account Name:</strong> {{ adPayment.distributor_account_name }}</p>
          </template>
          <template v-else-if="(adPayment.ad_pay_method || adPayment.pay_method) === 'Credit/Debit Card'">
            <p><strong>Card Details:</strong> {{ adPayment.ad_card_type || adPayment.card_type || '—' }} - ****{{ adPayment.ad_card_last4_digits || adPayment.card_last4_digits || '—' }}</p>
          </template>
          <template v-else-if="(adPayment.ad_pay_method || adPayment.pay_method) === 'Mobile Payment'">
            <p><strong>Mobile Payment:</strong> {{ adPayment.ad_mobile_payment_type || adPayment.mobile_payment_type || '—' }} - {{ adPayment.ad_mobile_provider || adPayment.mobile_provider || '—' }}</p>
          </template>
          <template v-else-if="(adPayment.ad_pay_method || adPayment.pay_method) === 'Online Payment'">
            <p><strong>Gateway:</strong> {{ adPayment.ad_online_gateway || adPayment.online_gateway || '—' }}</p>
          </template>

          <p v-if="adPayment.trans_ref_code"><strong>Ref Code:</strong> {{ adPayment.trans_ref_code }}</p>
        </div>

        <!-- Amount Table -->
        <table class="invoice-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Payment Method</th>
              <th class="text-right">Amount (Rs.)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Advance Payment</td>
              <td>{{ adPayment.ad_pay_method || adPayment.pay_method || '-' }}</td>
              <td class="text-right">{{ formatNumber(adPayment.advance_amount || adPayment.amount) }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Total -->
        <div class="total-section">
          <h3>Total Paid Amount: <span>Rs. {{ formatNumber(adPayment.advance_amount || adPayment.amount) }}</span></h3>
        </div>

        <!-- Signatures -->
        <div class="signatures">
          <div class="signature-box">
            <div class="line"></div>
            <p><strong>DSR / SE Signature</strong></p>
          </div>
          <div class="signature-box">
            <div class="line"></div>
            <p><strong>Dealer Signature</strong></p>
          </div>
        </div>

        <div class="receipt-footer">
           Generated on: {{ formatDateTime(new Date()) }}
        </div>
      </div>

      <!-- Actions -->
      <div class="invoice-actions" v-if="!isLoadingDetails && !fetchError">
        <button @click="$emit('close')" class="btn btn-secondary m-r-10" :disabled="isGeneratingPdf">Close</button>
        <button @click="printAdvancePayment" class="btn btn-primary" :disabled="isGeneratingPdf">
          <span v-if="isGeneratingPdf" class="spinner-border spinner-border-sm m-r-5" role="status" aria-hidden="true"></span>
          <i v-else class="fa fa-print m-r-5" aria-hidden="true"></i> 
          {{ isGeneratingPdf ? 'Generating...' : 'Print' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getAdvancePaymentDetailsById } from '@/services/dataService';

export default {
  props: {
    showModal: { type: Boolean, required: true },
    advancePaymentId: { type: [Number, String], required: false }
  },
  data() {
    return {
      isGeneratingPdf: false,
      isLoadingDetails: false,
      fetchError: null,
      adPayment: {},
      dealerDetails: null,
      distributorDetails: null,
      distributor: {
        full_name: localStorage.getItem('full_name') || '',
        address1: localStorage.getItem('address') || '',
        email: localStorage.getItem('email') || '',
        mobile: localStorage.getItem('mobile') || ''
      }
    };
  },
  watch: {
    showModal: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.advancePaymentId) {
           this.fetchDetails(this.advancePaymentId);
        } else if (!newVal) {
           this.adPayment = {};
           this.dealerDetails = null;
           this.distributorDetails = null;
           this.fetchError = null;
        }
      }
    }
  },
  methods: {
    async fetchDetails(id) {
       this.isLoadingDetails = true;
       this.fetchError = null;
       try {
          const token = localStorage.getItem('token_set');
          const response = await getAdvancePaymentDetailsById(id, token);
          if (response.data && response.data.status === 'success') {
             this.adPayment = response.data.data.advancePaymentDetails || {};
             this.dealerDetails = response.data.data.dealerDetails || {};
             this.distributorDetails = response.data.data.distributorDetails || null;
          } else {
             this.fetchError = response.data?.message || 'Failed to fetch details';
          }
       } catch (error) {
          console.error("Error fetching advance payment details", error);
          this.fetchError = 'An error occurred while fetching details.';
       } finally {
          this.isLoadingDetails = false;
       }
    },
    formatNumber(value) {
      if (!value) return '0.00';
      return parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDateTime(datetime) {
      if (!datetime) return '';
      const date = new Date(datetime);
      return date.toLocaleString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },
    printAdvancePayment() {
      if (this.isGeneratingPdf) return;
      this.isGeneratingPdf = true;

      try {
        const printWindow = window.open('', '_blank');
        
        const adPayment = this.adPayment;
        const paymentMethod = adPayment.ad_pay_method || adPayment.pay_method;
        const distName = this.distributorDetails?.businessName || this.distributorDetails?.full_name || this.distributor?.full_name || '—';
        const distAddress = this.distributorDetails?.address || this.distributor?.address1 || '—';
        const distMobile = this.distributorDetails?.mobile || this.distributor?.mobile || '—';

        const dealerName = this.dealerDetails?.businessName || adPayment.dealer_name || '—';
        const dealerAddress = this.dealerDetails?.address || '—';
        const dealerMobile = this.dealerDetails?.mobile || '—';

        const receiptNo = adPayment.advance_payments_no || '—';
        const date = this.formatDateTime(adPayment.ad_paid_at || adPayment.paid_at || adPayment.created_at);
        const generatedDate = this.formatDateTime(new Date());
        const amountStr = this.formatNumber(adPayment.advance_amount || adPayment.amount);

        let paymentDetailsHtml = '';
        if (paymentMethod === 'Cheque') {
          paymentDetailsHtml += `<div class="info-row"><span class="label">Cheque No:</span><span class="value">${adPayment.ad_cheque_no || adPayment.cheque_no || '—'}</span></div>`;
          paymentDetailsHtml += `<div class="info-row"><span class="label">Bank:</span><span class="value">${adPayment.bank_name || '—'}</span></div>`;
          paymentDetailsHtml += `<div class="info-row"><span class="label">Branch:</span><span class="value">${adPayment.branch_name || '—'}</span></div>`;
        } else if (paymentMethod === 'Bank Transfer') {
          paymentDetailsHtml += `<div class="info-row"><span class="label">Account No:</span><span class="value">${adPayment.ad_bank_acc_no || adPayment.distributor_account || '—'}</span></div>`;
          if (adPayment.distributor_account_name) {
            paymentDetailsHtml += `<div class="info-row"><span class="label">Account Name:</span><span class="value">${adPayment.distributor_account_name}</span></div>`;
          }
        } else if (paymentMethod === 'Credit/Debit Card') {
          paymentDetailsHtml += `<div class="info-row"><span class="label">Card Details:</span><span class="value">${adPayment.ad_card_type || adPayment.card_type || '—'} - ****${adPayment.ad_card_last4_digits || adPayment.card_last4_digits || '—'}</span></div>`;
        } else if (paymentMethod === 'Mobile Payment') {
          paymentDetailsHtml += `<div class="info-row"><span class="label">Mobile Payment:</span><span class="value">${adPayment.ad_mobile_payment_type || adPayment.mobile_payment_type || '—'} - ${adPayment.ad_mobile_provider || adPayment.mobile_provider || '—'}</span></div>`;
        } else if (paymentMethod === 'Online Payment') {
          paymentDetailsHtml += `<div class="info-row"><span class="label">Gateway:</span><span class="value">${adPayment.ad_online_gateway || adPayment.online_gateway || '—'}</span></div>`;
        }

        if (adPayment.trans_ref_code) {
          paymentDetailsHtml += `<div class="info-row"><span class="label">Ref Code:</span><span class="value">${adPayment.trans_ref_code}</span></div>`;
        }

        const logoImg = this.$refs.modalContent.querySelector('.logo');
        const logoSrc = logoImg ? logoImg.src : '';

        printWindow.document.write(`
          <html>
            <head>
              <title>Advance Payment Receipt</title>
              <style>
                * { box-sizing: border-box; margin: 0; padding: 0; }
                @page {
                  size: A5 portrait;
                  margin: 10mm;
                }
                body {
                  font-family: "Inter", "Helvetica", Arial, sans-serif;
                  font-size: 10pt;
                  color: #000;
                  padding: 0;
                  margin: 0;
                }
                .receipt-container {
                  display: flex;
                  flex-direction: column;
                  min-height: 95vh;
                }
                .header {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 15px;
                }
                .logo {
                  max-width: 140px;
                }
                .header-right {
                  text-align: right;
                }
                .title {
                  font-size: 14pt;
                  font-weight: bold;
                  margin-bottom: 5px;
                  color: #000;
                  text-transform: uppercase;
                }
                .header-info {
                  font-size: 9pt;
                  margin-bottom: 3px;
                }
                .divider {
                  border-top: 1px solid #ccc;
                  margin: 15px 0;
                }
                .info-section {
                  display: flex;
                  justify-content: space-between;
                  gap: 20px;
                }
                .info-box {
                  flex: 1;
                  word-break: break-word;
                }
                .info-box h4 {
                  font-size: 10pt;
                  font-weight: bold;
                  margin-bottom: 8px;
                  text-transform: uppercase;
                }
                .info-row {
                  display: flex;
                  margin-bottom: 4px;
                  font-size: 9pt;
                  line-height: 1.3;
                }
                .info-row .label {
                  font-weight: bold;
                  width: 60px;
                  flex-shrink: 0;
                }
                .info-row .value {
                  flex: 1;
                }
                .payment-info {
                  margin-top: 10px;
                  margin-bottom: 15px;
                }
                .payment-info h4 {
                  font-size: 10pt;
                  font-weight: bold;
                  margin-bottom: 8px;
                  text-transform: uppercase;
                }
                .payment-info .label {
                  width: 90px;
                }
                table {
                  width: 100%;
                  border-collapse: collapse;
                  margin-bottom: 15px;
                }
                th, td {
                  border: 1px solid #ccc;
                  padding: 8px;
                  font-size: 9pt;
                }
                th {
                  background-color: #f8f9fa !important;
                  font-weight: bold;
                  text-align: left;
                  -webkit-print-color-adjust: exact;
                }
                th.text-right, td.text-right {
                  text-align: right;
                }
                .total-amount {
                  font-size: 12pt;
                  font-weight: bold;
                  text-align: right;
                  margin-top: 15px;
                  margin-bottom: 40px;
                }
                .spacer {
                  flex-grow: 1; 
                }
                .signatures {
                  display: flex;
                  justify-content: space-between;
                  margin-top: 50px;
                  margin-bottom: 30px;
                }
                .sig-box {
                  width: 40%;
                  text-align: center;
                }
                .sig-line {
                  border-top: 1px solid #000;
                  margin-bottom: 8px;
                }
                .sig-text {
                  font-size: 9pt;
                  font-weight: bold;
                }
                .receipt-footer {
                  text-align: right;
                  font-size: 8pt;
                  font-style: italic;
                  color: #555;
                  margin-bottom: 10px;
                }
              </style>
            </head>
            <body>
              <div class="receipt-container">
                <div class="header">
                  ${logoSrc ? `<img src="${logoSrc}" alt="Logo" class="logo" />` : '<div></div>'}
                  <div class="header-right">
                    <div class="title">Advance Payment Receipt</div>
                    <div class="header-info"><strong>Receipt No:</strong> ${receiptNo}</div>
                    <div class="header-info"><strong>Date:</strong> ${date}</div>
                  </div>
                </div>
                
                <div class="divider"></div>
                
                <div class="info-section">
                  <div class="info-box">
                    <h4>Distributor Details</h4>
                    <div class="info-row"><span class="label">Name:</span><span class="value">${distName}</span></div>
                    <div class="info-row"><span class="label">Address:</span><span class="value">${distAddress}</span></div>
                    <div class="info-row"><span class="label">Mobile:</span><span class="value">${distMobile}</span></div>
                  </div>
                  <div class="info-box">
                    <h4>Dealer Details</h4>
                    <div class="info-row"><span class="label">Name:</span><span class="value">${dealerName}</span></div>
                    <div class="info-row"><span class="label">Address:</span><span class="value">${dealerAddress}</span></div>
                    <div class="info-row"><span class="label">Mobile:</span><span class="value">${dealerMobile}</span></div>
                  </div>
                </div>
                
                <div class="divider"></div>
                
                <div class="payment-info">
                  <h4>Payment Info</h4>
                  <div class="info-row"><span class="label">Method:</span><span class="value">${paymentMethod || '—'}</span></div>
                  ${paymentDetailsHtml}
                </div>
                
                <table>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Payment Method</th>
                      <th class="text-right">Amount (Rs.)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Advance Payment</td>
                      <td>${paymentMethod || '-'}</td>
                      <td class="text-right">${amountStr}</td>
                    </tr>
                  </tbody>
                </table>
                
                <div class="total-amount">
                  Total Paid Amount: Rs. ${amountStr}
                </div>
                
                <div class="spacer"></div>

                <div class="signatures">
                  <div class="sig-box">
                    <div class="sig-line"></div>
                    <div class="sig-text">DSR / SE Signature</div>
                  </div>
                  <div class="sig-box">
                    <div class="sig-line"></div>
                    <div class="sig-text">Dealer Signature</div>
                  </div>
                </div>
                
                <div class="receipt-footer">
                  Generated on: ${generatedDate}
                </div>
              </div>
            </body>
          </html>
        `);
        
        printWindow.document.close();
        
        // Timeout to allow logo image to load
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
          this.isGeneratingPdf = false;
        }, 500);

      } catch (e) {
        console.error("Error generating print window:", e);
        this.isGeneratingPdf = false;
      }
    }
  }
};
</script>

<style scoped>
/* Modal background */
.modal-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0,0,0,0.6);
  display: flex; justify-content: center; align-items: center;
  z-index: 1000;
}

/* Popup container */
.modal-content {
  background: #e2e8f0; 
  padding: 20px;
  border-radius: 12px;
  max-width: 650px;
  width: 95%;
  max-height: 95vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
  font-family: "Inter", Arial, sans-serif;
}

/* Loading and error */
.loading-container, .error-container {
  background: #fff;
  border-radius: 8px;
  padding: 40px 20px;
}

/* Receipt Preview Container (A5 Portrait simulation) */
.receipt-container {
  width: 148mm;
  min-height: 210mm;
  max-width: 100%;
  padding: 10mm;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  margin: 0 auto;
  box-sizing: border-box;
  position: relative;
}

/* Logo & Header */
.receipt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.logo {
  max-width: 100px;
}
.header-right p {
  margin: 2px 0;
  font-size: 11px;
  color: #555;
}
.invoice-title {
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin: 0 0 5px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.divider {
  border: 0;
  border-top: 1px solid #eee;
  margin: 15px 0;
}

/* Info Section */
.info-section {
  display: flex;
  justify-content: space-between;
}
.info-box h4 {
  font-size: 11px;
  font-weight: 700;
  color: #0056b3;
  text-transform: uppercase;
  margin-bottom: 8px;
  border-bottom: 1px solid #eee;
  padding-bottom: 4px;
}
.info-box p {
  margin: 4px 0;
  font-size: 11px;
  color: #333;
}
.right-align-box p {
  text-align: left;
}
.text-right {
  text-align: left;
}

/* Payment Details */
.payment-details h4 {
  font-size: 11px;
  font-weight: 700;
  color: #0056b3;
  text-transform: uppercase;
  margin-bottom: 8px;
  border-bottom: 1px solid #eee;
  padding-bottom: 4px;
}
.payment-details p {
  margin: 4px 0;
  font-size: 11px;
  color: #333;
}

/* Table */
.invoice-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  background: #fff;
}
.invoice-table th, .invoice-table td {
  border: 1px solid #eee;
  padding: 8px 6px;
  text-align: left;
  font-size: 11px;
}
.invoice-table th {
  background-color: #f8f9fa;
  font-weight: 700;
  color: #333;
}

/* Total Section */
.total-section {
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
}
.total-section h3 {
  margin: 0;
  font-size: 14px;
  color: #333;
}
.total-section span {
  font-weight: 700;
  color: #000;
  margin-left: 10px;
}

/* Signatures */
.signatures {
  display: flex;
  justify-content: space-between;
  margin-top: 50px;
}
.signature-box {
  text-align: center;
  width: 40%;
}
.signature-box .line {
  border-top: 1px dashed #999;
  margin-bottom: 8px;
}
.signature-box p {
  margin: 0;
  font-size: 11px;
  color: #555;
  font-weight: 600;
}

.receipt-footer {
  text-align: right;
  margin-top: 30px;
  font-size: 10px;
  color: #888;
  font-style: italic;
}

/* Actions */
.invoice-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 25px;
}
.btn {
  padding: 8px 20px;
  font-size: 13px;
  border-radius: 6px;
  cursor: pointer;
  border: none;
  font-weight: 600;
  transition: all 0.2s ease;
}
.btn-primary {
  background: #0056b3;
  color: #fff;
}
.btn-primary:hover {
  background: #004494;
}
.btn-secondary {
  background: #6c757d;
  color: #fff;
}
.btn-secondary:hover {
  background: #5a6268;
}
.m-r-10 { margin-right: 10px; }
.m-r-5 { margin-right: 5px; }
</style>
